import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, ArrowLeft, MapPin, Plus, Minus } from 'lucide-react';
import { Navigation } from './Navigation';
import { businessEntity, websiteEntity, injectJsonLd, setHeadMeta, canonicalUrl, BUSINESS_ID, WEBSITE_ID } from '../utils/seo';
export interface FAQItem {
  question: string;
  answer: string;
}
export interface BreadcrumbItem {
  name: string;
  item: string;
}
interface ServicePageLayoutProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  metaDescription: string;
  schema: object;
  /** Optional FAQ list. When provided, renders an accessible FAQ section and injects FAQPage schema. */
  faqs?: FAQItem[];
  /** Optional breadcrumb trail. When provided, renders a visible breadcrumb nav and injects BreadcrumbList schema. */
  breadcrumbs?: BreadcrumbItem[];
}
export function ServicePageLayout({
  title,
  subtitle,
  children,
  metaDescription,
  schema,
  faqs,
  breadcrumbs
}: ServicePageLayoutProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  // Scroll to top only when the page itself changes — not on every re-render
  // (e.g. opening an FAQ), which previously yanked the reader back to the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [title]);
  // Page props arrive as fresh object literals on every render, so key the
  // metadata effect off their serialised value instead of their identity.
  const metaKey = JSON.stringify({
    title,
    metaDescription,
    schema,
    faqs,
    breadcrumbs
  });
  useEffect(() => {
    const pageTitle = `${title} | Mike's Pro Detail`;
    const url = canonicalUrl(window.location.pathname);
    setHeadMeta({
      title: pageTitle,
      description: metaDescription,
      path: window.location.pathname,
      ogType: 'article'
    });
    // One linked structured-data graph per page: the canonical business and
    // website entities, this page's primary (Service/LocalBusiness) schema, and
    // optional FAQPage + BreadcrumbList — all cross-referenced by @id so search
    // engines resolve them to a single business rather than duplicates.
    const {
      ['@context']: _ignoredContext,
      ...primary
    } = schema as Record<string, unknown>;
    const primaryNode: Record<string, unknown> = {
      '@id': `${url}#primary`,
      isPartOf: {
        '@id': WEBSITE_ID
      },
      ...primary,
      mainEntityOfPage: url
    };
    // Point any inline provider at the single canonical business entity so the
    // site never declares competing LocalBusiness records.
    if (primaryNode.provider) {
      primaryNode.provider = {
        '@id': BUSINESS_ID
      };
    }
    const graph: object[] = [businessEntity, websiteEntity, primaryNode];
    if (faqs && faqs.length > 0) {
      graph.push({
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        isPartOf: {
          '@id': WEBSITE_ID
        },
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.answer
          }
        }))
      });
    }
    if (breadcrumbs && breadcrumbs.length > 0) {
      graph.push({
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: b.item
        }))
      });
    }
    return injectJsonLd(graph);
  }, [metaKey]);
  return <main className="min-h-screen w-full bg-black text-white selection:bg-red-600 selection:text-white" data-id="element-432" data-mp="3fknqb2kcdhgb">
      <Navigation data-id="element-433" data-mp="3kifhfud5wzjg" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 border-b border-zinc-900 overflow-hidden" data-id="element-434" data-mp="3khe2vfwxtuwz">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-red-900/20 blur-[120px] pointer-events-none rounded-full" data-id="element-435" data-mp="c22uow2jmo47" />

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-436" data-mp="2pbq13vd9rp2t">
          {/* Breadcrumb navigation */}
          {breadcrumbs && breadcrumbs.length > 0 ? <nav aria-label="Breadcrumb" className="mb-8" data-id="element-437" data-mp="2kaitbwb4mpw5">
              <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-400" data-id="element-438" data-mp="2fchbfepfswm9">
                {breadcrumbs.map((b, i) => <li key={b.item} className="flex items-center gap-2" data-id="element-439" data-mp="2aez7k2qa17l4" data-mpi={i}>
                    {i < breadcrumbs.length - 1 ? <Link to={b.item.replace('https://www.fulldetailpro.com', '') || '/'} className="hover:text-white transition-colors" data-id="element-440" data-mp="380mwy3obepn5">
                        {b.name}
                      </Link> : <span className="text-gray-300" aria-current="page" data-id="element-441" data-mp="2yb3ako1e8dnj">
                        {b.name}
                      </span>}
                    {i < breadcrumbs.length - 1 && <span className="text-zinc-600" data-id="element-442" data-mp="2taxmtiy8vbaq">/</span>}
                  </li>)}
              </ol>
            </nav> : <Link to="/" className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors group" data-id="element-443" data-mp="3m1fnyefer2li">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-444" data-mp="3uav9gsew0koe" />
              Back to Home
            </Link>}

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-445" data-mp="26kks2rlfhsf">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-6" data-id="element-446" data-mp="c5rzata2dih4">
              <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500 drop-shadow-lg" data-id="element-447" data-mp="h1h0f11qf7fj">
                {title}
              </span>
            </h1>
            <div className="h-1 w-24 bg-red-600 rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)] mb-6" data-id="element-448" data-mp="py7ux90mye80" />
            <p className="text-xl text-gray-400 leading-relaxed max-w-3xl" data-id="element-449" data-mp="uwt07muqvo3b">
              {subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Area */}
      <section className="py-16 px-4" data-id="element-450" data-mp="14qrujzoku3kp">
        <div className="max-w-4xl mx-auto" data-id="element-451" data-mp="f47dh2vviqa2">
          <div className="prose prose-invert prose-lg max-w-none prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mb-6 prose-h2:mt-12 prose-h3:text-2xl prose-h3:text-yellow-400 prose-p:text-gray-300 prose-p:leading-relaxed prose-li:text-gray-300 prose-strong:text-white" data-id="element-452" data-mp="f2w8izqozchd">
            {children}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs && faqs.length > 0 && <section className="py-16 px-4 border-t border-zinc-900" aria-labelledby="faq-heading" data-id="element-453" data-mp="sx6oosomhybt">
          <div className="max-w-4xl mx-auto" data-id="element-454" data-mp="17txt5gd5m08a">
            <h2 id="faq-heading" className="text-3xl md:text-4xl font-black uppercase tracking-tight mb-8 text-center" data-id="element-455" data-mp="17rlbl4bb5rvr">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4" data-id="element-456" data-mp="39lkm3nglw57e">
              {faqs.map((faq, index) => {
            const isOpen = openFaq === index;
            return <div key={index} className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden" data-id="element-457" data-mp="34oap9mqw0r9j" data-mpi={index}>
                    <button onClick={() => setOpenFaq(isOpen ? null : index)} className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-zinc-800/40 transition-colors" aria-expanded={isOpen} data-id="element-458" data-mp="1p2qlef9dhss7">
                      <span className="text-lg font-bold text-white" data-id="element-459" data-mp="1fcnfjeh5kkcg">
                        {faq.question}
                      </span>
                      {isOpen ? <Minus className="w-5 h-5 text-red-500 shrink-0" data-id="element-460" data-mp="11c4dcnzdc8i7" /> : <Plus className="w-5 h-5 text-red-500 shrink-0" data-id="element-461" data-mp="15f9eam3sw24q" />}
                    </button>
                    {isOpen && <div className="px-5 pb-5 text-gray-300 leading-relaxed" data-id="element-462" data-mp="rg9qths0xta5">
                        {faq.answer}
                      </div>}
                  </div>;
          })}
            </div>
          </div>
        </section>}

      {/* Service Areas SEO Block */}
      <section className="py-12 px-4 bg-zinc-900/30 border-t border-zinc-900" data-id="element-463" data-mp="2qsc8cmh38dl6">
        <div className="max-w-4xl mx-auto text-center" data-id="element-464" data-mp="2qpzqe9bby384">
          <MapPin className="w-8 h-8 text-red-500 mx-auto mb-4" data-id="element-465" data-mp="1dkg0hdoaqqsx" />
          <h2 className="text-2xl font-bold mb-4" data-id="element-466" data-mp="19k5tpysojwd0">
            Proudly Serving Ventura County
          </h2>
          <p className="text-gray-400 leading-relaxed" data-id="element-467" data-mp="14snp90rg7dna">
            We provide top-tier mobile auto detailing in{' '}
            <Link to="/ventura-county/camarillo" className="text-red-500 hover:underline" data-id="element-468" data-mp="zt1fx2eqqhjr">
              Camarillo
            </Link>
            ,{' '}
            <Link to="/ventura-county/oxnard" className="text-red-500 hover:underline" data-id="element-469" data-mp="usn7hj9895l7">
              Oxnard
            </Link>
            ,{' '}
            <Link to="/ventura-county/ventura" className="text-red-500 hover:underline" data-id="element-470" data-mp="lvcrjklgss9y">
              Ventura
            </Link>
            ,{' '}
            <Link to="/ventura-county/thousand-oaks" className="text-red-500 hover:underline" data-id="element-471" data-mp="gxl2cyc7fk9g">
              Thousand Oaks
            </Link>
            ,{' '}
            <Link to="/ventura-county/simi-valley" className="text-red-500 hover:underline" data-id="element-472" data-mp="2bakkay424qjw">
              Simi Valley
            </Link>
            ,{' '}
            <Link to="/ventura-county/moorpark" className="text-red-500 hover:underline" data-id="element-473" data-mp="2nrlphwgbwvif">
              Moorpark
            </Link>
            ,{' '}
            <Link to="/ventura-county/newbury-park" className="text-red-500 hover:underline" data-id="element-474" data-mp="2sj3myaj58dyn">
              Newbury Park
            </Link>
            , and{' '}
            <Link to="/ventura-county/westlake-village" className="text-red-500 hover:underline" data-id="element-475" data-mp="1zupqw0bm7rpq">
              Westlake Village
            </Link>
            . Whether you need car detailing in Camarillo or mobile detailing in
            Ventura County, we come directly to your home or office.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden border-t border-zinc-900" data-id="element-476" data-mp="3l79b779eol38">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/40 to-black pointer-events-none" data-id="element-477" data-mp="3l9lq1bjvafc2" />
        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-478" data-mp="3pap35qaj5x9l">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase mb-6" data-id="element-479" data-mp="1lzzyt2slgpts">
            Ready to Transform Your Vehicle?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-480" data-mp="1vqcqdeyttj9f">
            Book your professional mobile detailing appointment today. We bring
            the shop to you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-481" data-mp="131nqfj9ibjz9">
            <a href="tel:8183819905" className="flex items-center gap-3 px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-xl rounded-sm transition-all shadow-[0_0_20px_rgba(220,38,38,0.5)] hover:shadow-[0_0_30px_rgba(220,38,38,0.8)] hover:-translate-y-1 w-full sm:w-auto justify-center" data-id="element-482" data-mp="180ijde16uox3">
              <Phone className="w-6 h-6" data-id="element-483" data-mp="2oeqpjy1nilmk" />
              (818) 381-9905
            </a>
            <a href="/#contact-form" className="px-8 py-4 bg-white text-black hover:bg-gray-200 font-bold text-xl uppercase tracking-wider rounded-sm transition-all w-full sm:w-auto text-center" data-id="element-484" data-mp="2oh35k5numlwz">
              Get a Free Quote
            </a>
          </div>
        </div>
      </section>
    </main>;
}